/**
 * Company Detail — company file + residencies + attachment previews.
 * Feb 2026 / iter-181 additions:
 *  • Preview + download buttons (eye + download icons) for the license file.
 *  • Days-until-expiry badge on the license.
 *  • Preview + download + days-until-expiry on every residency card.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Building2, Calendar, MapPin, Phone, Mail, User, Trash2, IdCard, StickyNote,
  Eye, Download, FileText as FileIcon, X, UserPlus, Edit3, FileArchive, History, Loader2,
  Receipt, ExternalLink, Unlink, Link2, Save, Check, XCircle, Search, MoreVertical,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { AgentPageShell, ExpiryBadge } from './agent/AgentPageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPE_LABELS = {
  emirates_id: { ar: 'هوية إماراتية', en: 'Emirates ID', color: '#06b6d4' },
  residency:   { ar: 'إقامة',         en: 'Residency',   color: '#10b981' },
  visa:        { ar: 'تأشيرة',        en: 'Visa',        color: '#a855f7' },
};

// Renders a file preview modal — image inline, PDF as embedded <object>.
function PreviewModal({ open, onClose, base64, mime, name, language }) {
  if (!open) return null;
  const dataUrl = `data:${mime || 'application/octet-stream'};base64,${base64}`;
  const isImage = (mime || '').startsWith('image/');
  const isPdf = mime === 'application/pdf';
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-white font-semibold truncate">{name || (language === 'ar' ? 'معاينة' : 'Preview')}</div>
            <div className="text-xs text-[#808080]">{mime}</div>
          </div>
          <div className="flex gap-2">
            <a
              href={dataUrl}
              download={name || 'file'}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white"
              title={language === 'ar' ? 'تحميل' : 'Download'}
            >
              <Download className="h-4 w-4" />
            </a>
            <button onClick={onClose} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-[#050505] flex items-center justify-center">
          {isImage && <img src={dataUrl} alt={name} className="max-w-full max-h-full object-contain" />}
          {isPdf && (
            <object data={dataUrl} type="application/pdf" className="w-full h-[80vh]">
              <p className="text-[#808080] p-8 text-center">
                {language === 'ar' ? 'لا يمكن معاينة PDF — استخدم زر التحميل.' : "Can't preview PDF — use Download."}
              </p>
            </object>
          )}
          {!isImage && !isPdf && (
            <p className="text-[#808080] p-8 text-center">
              {language === 'ar' ? 'صيغة غير قابلة للمعاينة — استخدم زر التحميل.' : 'Format not previewable — use Download.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const AgentCompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [company, setCompany] = useState(null);
  const [residencies, setResidencies] = useState([]);
  const [otherDocs, setOtherDocs] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);   // iter-249
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {base64, mime, name}
  // iter-276 — main tab bar between license title & data card
  const [activeTab, setActiveTab] = useState('license');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewForm, setRenewForm] = useState({
    license_number: '', unified_license_number: '',
    issue_date: '', expiry_date: '',
    file: null,
  });
  const [renewing, setRenewing] = useState(false);
  const [renewExtracting, setRenewExtracting] = useState(false);
  // { extracted_ar, extracted_en, matched:boolean } — used to warn the admin
  // when the file appears to belong to a different company.
  const [renewNameCheck, setRenewNameCheck] = useState(null);

  // iter-213 (Feb 2026) — inline manual agent assignment from this page
  // (previously required a trip to the Edit page).
  const [agentModal, setAgentModal] = useState(false);
  const [agentForm, setAgentForm] = useState({ agent_name: '', agent_email: '', agent_phone: '' });
  const [savingAgent, setSavingAgent] = useState(false);

  // iter-213 — contact persons management (add / edit / delete).
  const [contactForm, setContactForm] = useState(null); // null | {id?, name, phone, email}
  const [savingContact, setSavingContact] = useState(false);

  // Fuzzy check: normalise (lowercase, strip punctuation/diacritics, remove
  // very common legal suffixes) and require at least one meaningful token
  // overlap between the extracted name and the company name on file.
  const _fuzzyMatchCompanyName = (extractedAr, extractedEn, companyAr, companyEn) => {
    const norm = (s) => {
      if (!s) return '';
      let x = String(s).toLowerCase();
      // remove Arabic diacritics
      x = x.replace(/[\u064B-\u0652\u0670]/g, '');
      // unify alef/hamza/ya/ta-marbuta variants
      x = x.replace(/[إأآا]/g, 'ا').replace(/[ىي]/g, 'ي').replace(/ة/g, 'ه');
      // strip common suffixes
      x = x.replace(/\b(fz[-\s]?llc|fzllc|fz|llc|l\.l\.c|ذ\.?م\.?م|ش\.م\.م|ش\.ذ\.م\.م|group|company|co\.?)\b/gi, ' ');
      // punctuation → space
      x = x.replace(/[^\p{L}\p{N}\s]/gu, ' ');
      return x.replace(/\s+/g, ' ').trim();
    };
    const tokens = (s) => norm(s).split(' ').filter(t => t.length >= 3);
    const setA = new Set([...tokens(extractedAr), ...tokens(extractedEn)]);
    const setB = new Set([...tokens(companyAr), ...tokens(companyEn)]);
    let overlap = 0;
    setA.forEach(t => { if (setB.has(t)) overlap += 1; });
    // Need at least 2 shared meaningful tokens (or 1 if the extracted name is very short).
    const minNeeded = Math.min(setA.size, setB.size) >= 2 ? 2 : 1;
    return overlap >= minNeeded;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agent/companies/${id}`, { headers: getAuthHeaders() });
      setCompany(res.data?.data || null);
      setResidencies(res.data?.residencies || []);
      setOtherDocs(res.data?.other_docs || []);
      // iter-249 — load service requests attached to this company file
      try {
        const svcRes = await axios.get(
          `${API}/admin/company-service-requests/by-company/${id}`,
          { headers: getAuthHeaders() },
        );
        setServiceRequests(svcRes.data?.data || []);
      } catch {
        setServiceRequests([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleDeleteResidency = async (rid) => {    if (!window.confirm(isAr ? 'حذف هذا السجل؟' : 'Delete this record?')) return;
    try {
      await axios.delete(`${API}/agent/residencies/${rid}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      setResidencies((r) => r.filter((x) => x.id !== rid));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  // iter-262 (Feb 2026) — Delete an "other document" from within the
  // company file. The list of other docs is now rendered directly on
  // this page, so we own the removal too.
  const handleDeleteOtherDoc = async (docId) => {
    if (!window.confirm(isAr ? 'حذف هذه الوثيقة؟' : 'Delete this document?')) return;
    try {
      await axios.delete(`${API}/agent/other-docs/${docId}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      setOtherDocs((d) => d.filter((x) => x.id !== docId));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  // iter-196 — Delete the entire company file (moved from the list card).
  // Cascade-deletes all its residencies + other docs on the backend.
  const handleDeleteCompany = async () => {
    if (!window.confirm(isAr
      ? 'حذف ملف الشركة نهائياً؟ سيُحذف مع جميع الإقامات والمستندات التابعة له.'
      : 'Delete this company file? All linked residencies and documents will also be removed.',
    )) return;
    try {
      await axios.delete(`${API}/agent/companies/${id}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم حذف ملف الشركة' : 'Company file deleted');
      navigate('/manage/cloudgm-agent/companies');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const openPreview = (base64, mime, name) => {
    if (!base64) {
      toast.error(isAr ? 'لا يوجد ملف مرفق' : 'No file attached');
      return;
    }
    setPreview({ base64, mime, name });
  };

  // iter-213 — save agent info inline (create OR update) without leaving the page.
  const handleSaveAgent = async () => {
    const name = (agentForm.agent_name || '').trim();
    if (!name) {
      toast.error(isAr ? 'اسم الوكيل مطلوب' : 'Agent name is required');
      return;
    }
    setSavingAgent(true);
    try {
      const res = await axios.patch(
        `${API}/agent/companies/${id}`,
        {
          agent_id: null, // manual = no linked user id
          agent_name: name,
          agent_email: (agentForm.agent_email || '').trim(),
          agent_phone: (agentForm.agent_phone || '').trim(),
        },
        { headers: getAuthHeaders() },
      );
      setCompany(res.data?.data || company);
      setAgentModal(false);
      toast.success(isAr ? 'تم حفظ بيانات الوكيل' : 'Agent saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSavingAgent(false);
    }
  };

  // iter-213 — remove the manually-set agent (only allowed when no task is linked).
  const handleRemoveAgent = async () => {
    if (!window.confirm(isAr ? 'إزالة الوكيل من هذا الملف؟' : 'Remove the agent from this file?')) return;
    try {
      const res = await axios.patch(
        `${API}/agent/companies/${id}`,
        { agent_id: null, agent_name: '', agent_email: '', agent_phone: '' },
        { headers: getAuthHeaders() },
      );
      setCompany(res.data?.data || company);
      toast.success(isAr ? 'تمت الإزالة' : 'Removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  // iter-213 — contact persons CRUD (single-field array on the company doc).
  const _uid = () => 'c_' + Math.random().toString(36).slice(2, 10);
  const persistContacts = async (nextList) => {
    setSavingContact(true);
    try {
      const res = await axios.patch(
        `${API}/agent/companies/${id}`,
        { contact_persons: nextList },
        { headers: getAuthHeaders() },
      );
      setCompany(res.data?.data || company);
      setContactForm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSavingContact(false);
    }
  };
  const handleSaveContact = async () => {
    if (!contactForm) return;
    const name  = (contactForm.name  || '').trim();
    const phone = (contactForm.phone || '').trim();
    const email = (contactForm.email || '').trim();
    if (!name && !phone && !email) {
      toast.error(isAr ? 'أدخل على الأقل اسم أو هاتف أو بريد' : 'Enter at least a name, phone or email');
      return;
    }
    const list = Array.isArray(company?.contact_persons) ? [...company.contact_persons] : [];
    if (contactForm.id) {
      const idx = list.findIndex((c) => c.id === contactForm.id);
      if (idx >= 0) list[idx] = { ...list[idx], name, phone, email };
    } else {
      list.push({ id: _uid(), name, phone, email });
    }
    await persistContacts(list);
    toast.success(isAr ? 'تم حفظ جهة الاتصال' : 'Contact saved');
  };
  const handleDeleteContact = async (cid) => {
    if (!window.confirm(isAr ? 'حذف جهة الاتصال؟' : 'Delete this contact?')) return;
    const list = (company?.contact_persons || []).filter((c) => c.id !== cid);
    await persistContacts(list);
    toast.success(isAr ? 'تم الحذف' : 'Deleted');
  };

  // iter-200 — unlink the currently-linked agent TASK from this company file.
  // (Previously we linked a tax invoice; the user asked to switch this to
  // "طلب الوكيل" which is an assigned task with reference AGNT-XXXXXX.)
  const handleUnlinkTask = async () => {
    if (!window.confirm(isAr ? 'فك ربط طلب الوكيل من هذا الملف؟' : 'Unlink the agent task from this company file?')) return;
    try {
      const res = await axios.patch(
        `${API}/agent/companies/${id}`,
        {
          linked_agent_task_id: null,
          linked_agent_task_ref: '',
          linked_agent_task_snapshot: null,
        },
        { headers: getAuthHeaders() },
      );
      setCompany(res.data?.data || company);
      toast.success(isAr ? 'تم فك الربط' : 'Unlinked');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'متابعة ملف الشركات', label_en: 'Track Company Files', path: '/manage/cloudgm-agent/companies' },
    { label_ar: company ? (company.company_name_ar || company.company_name_en) : '…',
      label_en: company ? (company.company_name_en || company.company_name_ar) : '…',
      path: null },
  ];

  if (loading) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/companies')}>
        <div className="text-center py-16 text-[#808080]">{isAr ? 'جاري التحميل…' : 'Loading…'}</div>
      </AgentPageShell>
    );
  }
  if (!company) {
    return (
      <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/companies')}>
        <div className="text-center py-16">
          <p className="text-[#808080] mb-4">{isAr ? 'الملف غير موجود' : 'Company file not found'}</p>
          <Button onClick={() => navigate('/manage/cloudgm-agent/companies')} variant="outline" className="border-[#2a2a4e] rounded-xl text-white">
            {isAr ? 'رجوع للقائمة' : 'Back to list'}
          </Button>
        </div>
      </AgentPageShell>
    );
  }

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/companies')}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 text-white" data-testid="agent-company-title">
            <Building2 className="h-6 w-6 text-emerald-400" />
            {isAr ? (company.company_name_ar || company.company_name_en) : (company.company_name_en || company.company_name_ar)}
          </h1>
          {(isAr ? company.company_name_en : company.company_name_ar) && (
            <p className="text-sm text-[#808080] mt-1">
              {isAr ? company.company_name_en : company.company_name_ar}
            </p>
          )}
        </div>

        {/* iter-276 — Actions collapsed into a single dropdown, per user request. */}
        <div className="relative">
          <Button
            onClick={() => setActionsOpen((v) => !v)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="agent-company-actions-btn"
          >
            <MoreVertical className="h-4 w-4 me-2" />
            {isAr ? 'إجراءات الملف' : 'File Actions'}
          </Button>
          {actionsOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setActionsOpen(false)} />
              <div
                className="absolute end-0 top-full mt-2 z-50 min-w-[240px] bg-[#0e0e18] border border-white/10 rounded-xl shadow-2xl p-2 space-y-1"
                data-testid="agent-company-actions-menu"
              >
                <ActionMenuItem
                  icon={Edit3}
                  onClick={() => { setActionsOpen(false); navigate(`/manage/cloudgm-agent/companies/${company.id}/edit`); }}
                  label={isAr ? 'تعديل البيانات' : 'Edit'}
                  testId="agent-company-edit-btn"
                />
                <ActionMenuItem
                  icon={History}
                  color="text-emerald-300"
                  onClick={() => { setActionsOpen(false); navigate(`/manage/cloudgm-agent/companies/${company.id}/renew-license`); }}
                  label={isAr ? 'تجديد الرخصة التجارية' : 'Renew Commercial License'}
                  testId="agent-company-renew-btn"
                />
                <ActionMenuItem
                  icon={Plus}
                  color="text-emerald-300"
                  onClick={() => { setActionsOpen(false); navigate(`/registered-companies-services/new?company_id=${company.id}`); }}
                  label={isAr ? 'إدراج طلب خدمة' : 'Insert Service Request'}
                  testId="agent-company-add-service-request-btn"
                />
                <ActionMenuItem
                  icon={Plus}
                  color="text-cyan-300"
                  onClick={() => { setActionsOpen(false); navigate(`/manage/cloudgm-agent/residency/new?company_id=${company.id}`); }}
                  label={isAr ? 'إضافة إقامة / هوية / تأشيرة' : 'Add Residency / ID / Visa'}
                  testId="agent-add-residency-here-btn"
                />
                <ActionMenuItem
                  icon={FileArchive}
                  color="text-yellow-300"
                  onClick={() => { setActionsOpen(false); navigate(`/manage/cloudgm-agent/other-docs?company_id=${company.id}`); }}
                  label={isAr ? 'إضافة وثائق أخرى' : 'Add Other Documents'}
                  testId="agent-add-other-docs-here-btn"
                />
                <div className="h-px bg-white/10 my-1" />
                <ActionMenuItem
                  icon={Trash2}
                  color="text-red-300"
                  onClick={() => { setActionsOpen(false); handleDeleteCompany(); }}
                  label={isAr ? 'حذف ملف الشركة' : 'Delete Company File'}
                  testId="agent-company-delete-btn"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* iter-276 + iter-278 — Tab switcher between license title & license data card.
         iter-278 adds "طلبات تجديد الرخصة" between License Data and Merchant Linking. */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="agent-company-tabs">
        {[
          { id: 'license',    ar: 'بيانات الرخصة',                en: 'License Data',       icon: FileIcon },
          { id: 'renewals',   ar: 'طلبات تجديد الرخصة',           en: 'License Renewals',   icon: History },
          { id: 'merchant',   ar: 'الربط بمنصة التجار',           en: 'Merchant Linking',   icon: Link2 },
          { id: 'services',   ar: 'سجل الطلبات والخدمات',         en: 'Requests & Services',icon: History },
          { id: 'residencies',ar: 'الإقامات والتأشيرات والهويات',  en: 'Residencies & IDs',  icon: IdCard },
          { id: 'documents',  ar: 'وثائق أخرى',                    en: 'Other Documents',    icon: FileArchive },
        ].map(({ id: tabId, ar, en, icon: Icon }) => (
          <button
            key={tabId}
            onClick={() => setActiveTab(tabId)}
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border transition-colors ${
              activeTab === tabId
                ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200'
                : 'bg-white/[0.02] border-white/10 text-white/60 hover:text-white hover:border-white/25'
            }`}
            data-testid={`agent-company-tab-${tabId}`}
          >
            <Icon className="h-4 w-4" />
            <span className="text-sm">{isAr ? ar : en}</span>
          </button>
        ))}
      </div>

      {/* iter-276 — Tab: License Data (default). Includes activities,
         contacts, linked agent task, linked registration, linked agent. */}
      {activeTab === 'license' && (<>
      {/* iter-279c — License card enlarged (hero-style expiry + bigger fields). */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-emerald-500/[0.06] to-white/[0.02] border border-emerald-500/20 rounded-3xl p-6 md:p-8 mb-6 shadow-lg shadow-emerald-500/5"
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <FileIcon className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white leading-tight">
                {isAr ? 'الرخصة التجارية' : 'Trade License'}
              </h2>
              {company.license_number && (
                <p className="text-sm text-emerald-200/80 font-mono mt-0.5">{company.license_number}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ExpiryBadge dateStr={company.expiry_date} language={language} />
            {company.license_file && (
              <>
                <Button
                  onClick={() => openPreview(company.license_file, company.license_file_mime, company.license_file_name || 'license')}
                  variant="outline"
                  className="border-[#2a2a4e] rounded-lg text-white hover:bg-white/5"
                  data-testid="agent-preview-license"
                >
                  <Eye className="h-4 w-4 me-2" />
                  {isAr ? 'معاينة' : 'Preview'}
                </Button>
                <a
                  href={`data:${company.license_file_mime || 'application/octet-stream'};base64,${company.license_file}`}
                  download={company.license_file_name || 'license'}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#2a2a4e] text-white hover:bg-white/5"
                  data-testid="agent-download-license"
                >
                  <Download className="h-4 w-4" />
                  {isAr ? 'تحميل' : 'Download'}
                </a>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 text-sm">
          <InfoRow label={isAr ? 'الرقم الموحد' : 'Unified license no.'} value={company.unified_license_number} />
          <InfoRow label={isAr ? 'جهة الإصدار' : 'Issuing authority'} value={company.issuing_authority} />
          <InfoRow label={isAr ? 'نوع الرخصة' : 'License type'} value={company.license_type} />
          <InfoRow label={isAr ? 'الشكل القانوني' : 'Legal form'} value={company.legal_form} />
          <InfoRow label={isAr ? 'رأس المال' : 'Capital'} value={company.capital} />
          <InfoRow label={isAr ? 'تاريخ الإصدار' : 'Issue date'} value={company.issue_date} icon={Calendar} />
          <InfoRow label={isAr ? 'تاريخ الانتهاء' : 'Expiry date'} value={company.expiry_date} icon={Calendar} />
          <InfoRow label={isAr ? 'ص.ب' : 'PO box'} value={company.po_box} />
          <InfoRow label={isAr ? 'الهاتف' : 'Phone'} value={company.phone} icon={Phone} />
          <InfoRow label={isAr ? 'الإيميل' : 'Email'} value={company.email} icon={Mail} full />
          <InfoRow label={isAr ? 'العنوان' : 'Address'} value={company.address} icon={MapPin} full />
        </div>
      </motion.section>
      </>)}

      {/* iter-278 — Tab: License renewal requests (list + "New renewal" button) */}
      {activeTab === 'renewals' && (
        <RenewalsListSection
          companyId={company.id}
          onNew={() => navigate(`/manage/cloudgm-agent/companies/${company.id}/renew-license`)}
          onOpen={(rid) => navigate(`/manage/cloudgm-agent/companies/${company.id}/renewals/${rid}`)}
          isAr={isAr}
        />
      )}

      {/* iter-276 — Tab: Merchant platform linking (multi-account) */}
      {activeTab === 'merchant' && (
        <PlatformLinkingCard
          company={company}
          onSaved={(patched) => setCompany((c) => ({ ...c, ...patched }))}
          isAr={isAr}
        />
      )}

      {/* iter-276 — Tab: Services & Requests */}
      {activeTab === 'services' && (
        <CompanyServiceRequestsSection
          requests={serviceRequests}
          isAr={isAr}
          onAdd={() => navigate(`/registered-companies-services/new?company_id=${company.id}`)}
          onOpen={(req) => navigate(`/registered-companies-services/${req.id}`)}
        />
      )}

      {/* iter-276 — Removed inline PlatformLinkingCard + services section
         from the license tab; they now live inside their own tabs. */}
      {false && (<>
      <PlatformLinkingCard
        company={company}
        onSaved={(patched) => setCompany((c) => ({ ...c, ...patched }))}
        isAr={isAr}
      />

      {/* iter-249 (Feb 2026) — Service Requests History tab.
         Replaces iter-248's paid-service timeline with a lifecycle-aware
         history table: request no, service, current stage, invoice link,
         and deep-link to the tracking pipeline. */}
      <CompanyServiceRequestsSection
        requests={serviceRequests}
        isAr={isAr}
        onAdd={() => navigate(`/registered-companies-services/new?company_id=${company.id}`)}
        onOpen={(req) => navigate(`/registered-companies-services/${req.id}`)}
      />
      </>)}

      {/* iter-276 — everything below (linked agent/task/reg/contacts/activities)
         stays on the "License Data" tab. */}
      {activeTab === 'license' && (<>

      {/* Linked Tax Invoice (iter-197) — sits between license and agent info */}
      {/* iter-237.5 (Feb 2026) — Removed the standalone "Company Agent" read-
         only card that duplicated the info already shown in the
         "Linked Agent" card below. The Linked Agent card is the single
         source of truth: it renders the same info AND exposes Edit / Remove
         controls, so keeping both was confusing and violated the user's
         "one agent card only" rule. */}

      {/* Linked Agent Task (iter-200)
         Replaces the previous "Linked Tax Invoice" card. When an agent is set
         on the company, we now link this file to the agent's manual assigned
         task (reference AGNT-XXXXXX). The tax-invoice fields in Mongo are kept
         for backwards-compat but no longer written / displayed here. */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6"
        data-testid="linked-agent-task-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <User className="h-5 w-5 text-amber-400" />
          <h2 className="font-semibold text-white">
            {isAr ? 'الوكيل المرتبط' : 'Linked Agent'}
          </h2>
        </div>

        {/* Case 1 — no agent set: allow inline manual assignment (iter-213) */}
        {!(company.agent_id || company.agent_name || company.agent_email) && (
          <div
            className="flex items-start justify-between gap-3 flex-wrap p-3 rounded-lg bg-[#050505] border border-white/5"
            data-testid="linked-agent-no-agent"
          >
            <div className="text-xs text-[#a0a0a0] leading-relaxed flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-amber-300 shrink-0" />
              {isAr
                ? 'لا يوجد وكيل مرتبط بعد. يمكنك إضافته يدوياً الآن، أو سيُربط تلقائياً عند إنشاء طلب.'
                : 'No agent linked yet. Add one manually now, or it will be filled automatically when a task is created.'}
            </div>
            <Button
              onClick={() => { setAgentForm({ agent_name: '', agent_email: '', agent_phone: '' }); setAgentModal(true); }}
              className="bg-amber-500/20 border border-amber-500/40 text-amber-200 hover:bg-amber-500/30 rounded-lg"
              data-testid="linked-agent-add-btn"
            >
              <UserPlus className="h-4 w-4 me-2" />
              {isAr ? 'إضافة وكيل' : 'Add agent'}
            </Button>
          </div>
        )}

        {/* Case 2 — agent is set (manually or linked) */}
        {(company.agent_id || company.agent_name || company.agent_email) && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="linked-agent-info">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 space-y-1">
                <div className="text-white font-semibold flex items-center gap-2">
                  <User className="h-4 w-4 text-amber-300" />
                  <span className="text-amber-100">{company.agent_name || (isAr ? '(بلا اسم)' : '(no name)')}</span>
                  {company.agent_id && (
                    <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
                      {isAr ? 'مسجَّل' : 'Registered'}
                    </span>
                  )}
                  {!company.agent_id && (
                    <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-white/10 border border-white/20 text-white/70">
                      {isAr ? 'يدوي' : 'Manual'}
                    </span>
                  )}
                </div>
                {company.agent_email && (
                  <div className="text-xs text-amber-100/80 inline-flex items-center gap-1.5">
                    <Mail className="h-3 w-3" />{company.agent_email}
                  </div>
                )}
                {company.agent_phone && (
                  <div className="text-xs text-amber-100/80 inline-flex items-center gap-1.5" dir="ltr">
                    <Phone className="h-3 w-3" />{company.agent_phone}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAgentForm({
                      agent_name:  company.agent_name  || '',
                      agent_email: company.agent_email || '',
                      agent_phone: company.agent_phone || '',
                    });
                    setAgentModal(true);
                  }}
                  className="border-amber-500/40 rounded-lg text-amber-300 hover:bg-amber-500/20"
                  data-testid="linked-agent-edit-btn"
                >
                  <Edit3 className="h-4 w-4 me-2" />
                  {isAr ? 'تعديل' : 'Edit'}
                </Button>
                {!company.linked_agent_task_id && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveAgent}
                    className="border-red-500/40 rounded-lg text-red-300 hover:bg-red-500/10"
                    data-testid="linked-agent-remove-btn"
                  >
                    <Trash2 className="h-4 w-4 me-2" />
                    {isAr ? 'إزالة' : 'Remove'}
                  </Button>
                )}
              </div>
            </div>

            {/* Linked task info (if any) — shown as a sub-block */}
            {company.linked_agent_task_id && (
              <div className="mt-3 pt-3 border-t border-amber-500/20" data-testid="linked-agent-task-linked">
                <div className="text-[11px] text-amber-100/60 mb-1">{isAr ? 'الطلب المرتبط:' : 'Linked task:'}</div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-amber-200 font-mono text-sm">
                      {company.linked_agent_task_ref || company.linked_agent_task_id}
                    </div>
                    {company.linked_agent_task_snapshot?.service_name_ar && (
                      <div className="text-[11px] text-amber-100/70 mt-0.5">
                        {isAr ? company.linked_agent_task_snapshot.service_name_ar
                              : (company.linked_agent_task_snapshot.service_name_en
                                 || company.linked_agent_task_snapshot.service_name_ar)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/dashboard/cloudgm/assigned-tasks/${company.linked_agent_task_id}`)}
                      className="border-amber-500/40 rounded-lg text-amber-300 hover:bg-amber-500/20"
                      data-testid="linked-agent-task-open-btn"
                    >
                      <ExternalLink className="h-4 w-4 me-2" />
                      {isAr ? 'فتح الطلب' : 'Open'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleUnlinkTask}
                      className="border-red-500/40 rounded-lg text-red-300 hover:bg-red-500/10"
                      data-testid="linked-agent-task-unlink-btn"
                    >
                      <Unlink className="h-4 w-4 me-2" />
                      {isAr ? 'فك الربط' : 'Unlink'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* iter-259 (Feb 2026) — "Link task" button removed per user's
               request: the dedicated "Linked Registration Number" card
               (below) replaces this workflow entirely. */}
          </div>
        )}
      </motion.section>

      {/* iter-237.2 (Feb 2026) — Linked company-registration request.
          Only rendered when the company file was promoted from a
          `company_registrations` record via "Open company file? Yes". */}
      {company.linked_registration_id && (
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-emerald-500/[0.05] border border-emerald-500/25 rounded-2xl p-5 mb-6"
          data-testid="linked-registration-card"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <FileIcon className="h-5 w-5 text-emerald-300 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] text-emerald-200/70 uppercase tracking-wide">
                  {isAr ? 'رقم الطلب المرتبط' : 'Linked registration'}
                </div>
                <div className="text-emerald-100 font-mono text-sm truncate">
                  {company.linked_registration_ref || company.linked_registration_id}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(`/manage/cloudgm-agent/company-registration/track/${company.linked_registration_id}`)}
              className="border-emerald-500/40 rounded-lg text-emerald-200 hover:bg-emerald-500/15"
              data-testid="linked-registration-open-btn"
            >
              <ExternalLink className="h-4 w-4 me-2" />
              {isAr ? 'فتح الطلب الأصلي' : 'Open original request'}
            </Button>
          </div>
        </motion.section>
      )}

      {/* iter-213 — Contact Persons Card */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.055 }}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6"
        data-testid="contact-persons-card"
      >
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-cyan-400" />
            <h2 className="font-semibold text-white">
              {isAr ? 'بيانات التواصل' : 'Contact Persons'}
            </h2>
            <span className="text-xs text-white/40 font-normal">
              ({(company.contact_persons || []).length})
            </span>
          </div>
          <Button
            onClick={() => setContactForm({ name: '', phone: '', email: '' })}
            className="bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 rounded-lg"
            data-testid="contact-add-btn"
          >
            <Plus className="h-4 w-4 me-2" />
            {isAr ? 'إضافة شخص' : 'Add person'}
          </Button>
        </div>

        {(company.contact_persons || []).length === 0 ? (
          <div className="text-xs text-[#808080] bg-[#050505] border border-white/5 rounded-lg px-3 py-3 text-center">
            {isAr
              ? 'لا يوجد أشخاص للتواصل بعد. يمكن إضافة أكثر من شخص (اسم/هاتف/بريد).'
              : 'No contact persons yet. You can add multiple people (name / phone / email).'}
          </div>
        ) : (
          <div className="space-y-2">
            {(company.contact_persons || []).map((c) => (
              <div
                key={c.id}
                className="flex items-start justify-between gap-3 flex-wrap p-3 rounded-lg bg-[#050505] border border-white/5"
                data-testid={`contact-row-${c.id}`}
              >
                <div className="min-w-0 space-y-0.5 flex-1">
                  {c.name && (
                    <div className="text-white text-sm font-medium inline-flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-cyan-400" />{c.name}
                    </div>
                  )}
                  {c.phone && (
                    <div className="text-xs text-white/70 inline-flex items-center gap-1.5 me-3" dir="ltr">
                      <Phone className="h-3 w-3" />{c.phone}
                    </div>
                  )}
                  {c.email && (
                    <div className="text-xs text-white/70 inline-flex items-center gap-1.5">
                      <Mail className="h-3 w-3" />{c.email}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => setContactForm({ id: c.id, name: c.name || '', phone: c.phone || '', email: c.email || '' })}
                    className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-cyan-300"
                    title={isAr ? 'تعديل' : 'Edit'}
                    data-testid={`contact-edit-${c.id}`}
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteContact(c.id)}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-white/60 hover:text-red-400"
                    title={isAr ? 'حذف' : 'Delete'}
                    data-testid={`contact-delete-${c.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.section>
      {(company.activities?.length > 0 || company.partners?.length > 0 || company.notes) && (
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6"
        >
          {company.activities?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[#606060] mb-2">{isAr ? 'الأنشطة' : 'Activities'}</p>
              <div className="flex flex-wrap gap-1.5">
                {company.activities.map((a, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs">{a}</span>
                ))}
              </div>
            </div>
          )}
          {company.partners?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-[#606060] mb-2">{isAr ? 'الشركاء' : 'Partners'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {company.partners.map((p, i) => (
                  <div key={i} className="px-3 py-2 rounded-lg bg-[#050505] border border-white/5 flex items-center justify-between text-xs">
                    <span className="text-white">{p.name || '—'}</span>
                    {p.share && <span className="text-[#808080]">{p.share}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {company.notes && (
            <div>
              <p className="text-xs text-[#606060] mb-1 flex items-center gap-1"><StickyNote className="h-3 w-3" /> {isAr ? 'ملاحظات' : 'Notes'}</p>
              <p className="text-sm text-[#c0c0c0] whitespace-pre-wrap">{company.notes}</p>
            </div>
          )}
        </motion.section>
      )}

      </>)}

      {/* iter-276 — Tab: Residencies + Visas + IDs */}
      {activeTab === 'residencies' && (
        <ResidencyAndOtherDocsSections
          residencies={residencies}
          otherDocs={[]}
          isAr={isAr}
          language={language}
          onAddResidency={() => navigate(`/manage/cloudgm-agent/residency/new?company_id=${company.id}`)}
          onDeleteResidency={handleDeleteResidency}
          onDeleteOtherDoc={handleDeleteOtherDoc}
          openPreview={openPreview}
          hideOtherDocs
        />
      )}

      {/* iter-276 — Tab: Other Documents */}
      {activeTab === 'documents' && (
        <ResidencyAndOtherDocsSections
          residencies={[]}
          otherDocs={otherDocs}
          isAr={isAr}
          language={language}
          onAddResidency={() => navigate(`/manage/cloudgm-agent/residency/new?company_id=${company.id}`)}
          onDeleteResidency={handleDeleteResidency}
          onDeleteOtherDoc={handleDeleteOtherDoc}
          openPreview={openPreview}
          hideResidencies
        />
      )}

      {/* iter-279c — Commercial License Registry (unified: current + history).
         Current license appears first, followed by expired renewals sorted
         newest-first. Enables the auto-injection flow (point C+D) where a
         completed renewal request drops a new license into this timeline. */}
      {activeTab === 'license' && (
        (() => {
          const historyRows = Array.isArray(company.license_history) ? company.license_history : [];
          const currentRow = company.license_number || company.license_file ? [{
            _current: true,
            id:                'current',
            license_number:    company.license_number,
            unified_license_number: company.unified_license_number,
            issue_date:        company.issue_date,
            expiry_date:       company.expiry_date,
            license_file:      company.license_file,
            license_file_mime: company.license_file_mime,
            license_file_name: company.license_file_name,
          }] : [];
          const sortedHistory = [...historyRows].sort((a, b) =>
            (b.expiry_date || '').localeCompare(a.expiry_date || ''));
          const allRows = [...currentRow, ...sortedHistory];
          if (!allRows.length) return null;
          return (
            <>
              <div className="flex items-center justify-between mt-6 mb-3">
                <h2 className="text-lg font-bold flex items-center gap-2 text-white">
                  <History className="h-5 w-5 text-[#a0a0a0]" />
                  {isAr ? 'سجل الرخص التجارية' : 'Commercial License Registry'}
                  <span className="text-xs text-[#808080]">({allRows.length})</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="license-registry">
                {allRows.map((h) => {
                  const isCurrent = !!h._current;
                  return (
                    <div key={h.id}
                         className={`rounded-2xl p-4 border ${isCurrent
                           ? 'bg-emerald-500/[0.06] border-emerald-500/30'
                           : 'bg-white/[0.02] border-white/10 opacity-90'}`}
                         data-testid={`license-registry-row-${h.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`px-2 py-0.5 rounded-md text-[10px] border ${isCurrent
                              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
                              : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
                              {isCurrent ? (isAr ? 'الحالية' : 'Current')
                                         : (isAr ? 'منتهية' : 'Expired')}
                            </span>
                            {h.license_number && <span className="text-sm text-white font-mono">{h.license_number}</span>}
                          </div>
                          <div className="text-xs text-[#a0a0a0] space-y-0.5">
                            {h.unified_license_number && <div>{isAr ? 'الرقم الموحد:' : 'Unified:'} <span className="font-mono">{h.unified_license_number}</span></div>}
                            {h.issue_date  && <div>{isAr ? 'إصدار:'   : 'Issue:'}  {h.issue_date}</div>}
                            {h.expiry_date && <div>{isAr ? 'انتهاء:'  : 'Expiry:'} {h.expiry_date}</div>}
                            {h.archived_at && <div className="text-[10px] text-[#606060]">
                              {isAr ? 'أرشفة:' : 'Archived:'} {h.archived_at.slice(0, 10)}
                            </div>}
                          </div>
                        </div>
                        {h.license_file && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => openPreview(h.license_file, h.license_file_mime,
                                                        h.license_file_name || `license-${h.license_number || h.id}`)}
                              className="p-1.5 rounded-md text-cyan-400 hover:bg-cyan-500/10"
                              title={isAr ? 'معاينة' : 'Preview'}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <a
                              href={`data:${h.license_file_mime || 'application/octet-stream'};base64,${h.license_file}`}
                              download={h.license_file_name || 'license'}
                              className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"
                              title={isAr ? 'تحميل' : 'Download'}
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()
      )}

      {/* iter-276 — Legacy "Previous Licenses" replaced by the unified registry above. */}
      {false && activeTab === 'license' && company.license_history?.length > 0 && (
        <>
          <div className="flex items-center justify-between mt-6 mb-3">
            <h2 className="text-lg font-bold flex items-center gap-2 text-white">
              <History className="h-5 w-5 text-[#808080]" />
              {isAr ? 'سجل الرخص السابقة' : 'Previous Licenses'}
              <span className="text-xs text-[#808080]">({company.license_history.length})</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {company.license_history.map((h) => (
              <div key={h.id} className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 opacity-90">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="px-2 py-0.5 rounded-md text-[10px] bg-red-500/10 border border-red-500/30 text-red-300">
                        {isAr ? 'منتهية' : 'Expired'}
                      </span>
                      {h.license_number && <span className="text-sm text-white">{h.license_number}</span>}
                    </div>
                    <div className="text-xs text-[#808080] space-y-0.5">
                      {h.issue_date  && <div>{isAr ? 'إصدار:' : 'Issue:'} {h.issue_date}</div>}
                      {h.expiry_date && <div>{isAr ? 'انتهاء:' : 'Expiry:'} {h.expiry_date}</div>}
                      {h.archived_at && <div className="text-[10px] text-[#606060]">{isAr ? 'أرشفة:' : 'Archived:'} {h.archived_at.slice(0, 10)}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {h.license_file && (
                      <>
                        <button
                          onClick={() => openPreview(h.license_file, h.license_file_mime, h.license_file_name || `license-${h.license_number || h.id}`)}
                          className="p-1.5 rounded-md text-cyan-400 hover:bg-cyan-500/10"
                          title={isAr ? 'معاينة' : 'Preview'}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <a
                          href={`data:${h.license_file_mime || 'application/octet-stream'};base64,${h.license_file}`}
                          download={h.license_file_name || 'previous-license'}
                          className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"
                          title={isAr ? 'تحميل' : 'Download'}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Renew License modal (iter-182) */}
      {renewOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => (!renewing && setRenewOpen(false))}>
          <div
            className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-lg w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <History className="h-5 w-5 text-emerald-400" />
              {isAr ? 'إضافة رخصة مُجدَّدة' : 'Add renewed license'}
            </h3>
            <p className="text-xs text-[#808080]">
              {isAr
                ? 'سيتم أرشفة الرخصة الحالية إلى "سجل الرخص السابقة" وستصبح الرخصة المرفوعة هي الحالية.'
                : 'The current license will move to "Previous Licenses" and the uploaded one will become active.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#808080]">{isAr ? 'رقم الرخصة الجديد' : 'New license #'}</label>
                <input
                  value={renewForm.license_number}
                  onChange={(e) => setRenewForm((f) => ({ ...f, license_number: e.target.value }))}
                  className="w-full mt-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="agent-renew-license-number"
                />
              </div>
              <div>
                <label className="text-xs text-[#808080]">{isAr ? 'الرقم الموحد' : 'Unified #'}</label>
                <input
                  value={renewForm.unified_license_number}
                  onChange={(e) => setRenewForm((f) => ({ ...f, unified_license_number: e.target.value }))}
                  className="w-full mt-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-[#808080]">{isAr ? 'تاريخ الإصدار' : 'Issue date'}</label>
                <input
                  type="date"
                  value={renewForm.issue_date}
                  onChange={(e) => setRenewForm((f) => ({ ...f, issue_date: e.target.value }))}
                  className="w-full mt-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-[#808080]">{isAr ? 'تاريخ الانتهاء' : 'Expiry date'}</label>
                <input
                  type="date"
                  value={renewForm.expiry_date}
                  onChange={(e) => setRenewForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  className="w-full mt-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="agent-renew-expiry-date"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#808080]">{isAr ? 'ملف الرخصة الجديدة (إلزامي)' : 'New license file (required)'}</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const reader = new FileReader();
                  reader.onload = async () => {
                    const [prefix, base64] = String(reader.result).split(',');
                    const m = /^data:([^;]+);base64$/.exec(prefix);
                    const mime = m ? m[1] : f.type;
                    setRenewForm((v) => ({ ...v, file: { base64, mime, name: f.name } }));
                    // Auto-extract via user's own Gemini key (feb 2026 / iter-183).
                    setRenewExtracting(true);
                    setRenewNameCheck(null);
                    try {
                      const res = await axios.post(
                        `${API}/agent/companies/extract-license`,
                        { file_base64: base64, mime },
                        { headers: getAuthHeaders(), timeout: 60_000 },
                      );
                      const data = res.data?.data || {};
                      setRenewForm((v) => ({
                        ...v,
                        license_number:         data.license_number         || v.license_number,
                        unified_license_number: data.unified_license_number || v.unified_license_number,
                        issue_date:             data.issue_date             || v.issue_date,
                        expiry_date:            data.expiry_date            || v.expiry_date,
                      }));
                      const matched = _fuzzyMatchCompanyName(
                        data.company_name_ar,
                        data.company_name_en,
                        company?.name_ar || company?.company_name_ar,
                        company?.name_en || company?.company_name_en,
                      );
                      setRenewNameCheck({
                        extracted_ar: data.company_name_ar || '',
                        extracted_en: data.company_name_en || '',
                        matched,
                      });
                      toast.success(isAr ? 'تم استخراج بيانات الرخصة بالذكاء' : 'License data extracted by AI');
                    } catch (err) {
                      // iter-244 — Handle structured "not trade license" 400.
                      const detail = err.response?.data?.detail;
                      let msg = isAr ? 'فشل استخراج بيانات الرخصة' : 'License extraction failed';
                      if (detail && typeof detail === 'object') msg = detail.message || msg;
                      else if (typeof detail === 'string') msg = detail;
                      toast.error(msg);
                    } finally {
                      setRenewExtracting(false);
                    }
                  };
                  reader.readAsDataURL(f);
                }}
                className="w-full mt-1 text-sm text-[#808080]"
                data-testid="agent-renew-file-input"
              />
              {renewForm.file && !renewExtracting && (
                <p className="text-xs text-emerald-400 mt-1">{renewForm.file.name}</p>
              )}
              {renewExtracting && (
                <p className="text-xs text-cyan-300 mt-1 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {isAr ? 'جاري استخراج بيانات الرخصة بالذكاء الاصطناعي…' : 'Extracting license data with AI…'}
                </p>
              )}
              {renewNameCheck && !renewNameCheck.matched && (
                <div
                  className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 space-y-1"
                  data-testid="agent-renew-name-mismatch"
                >
                  <div className="font-semibold">
                    ⚠️ {isAr ? 'تحذير — اسم الشركة في الملف لا يطابق' : 'Warning — company name in file doesn\'t match'}
                  </div>
                  <div className="text-amber-100/90 leading-relaxed">
                    <div>
                      {isAr ? 'في الملف:' : 'In file:'}{' '}
                      <span className="text-white">{renewNameCheck.extracted_ar || renewNameCheck.extracted_en || '—'}</span>
                    </div>
                    <div>
                      {isAr ? 'في الشركة الحالية:' : 'Current company:'}{' '}
                      <span className="text-white">{(company?.name_ar || company?.company_name_ar || '—')}</span>
                    </div>
                  </div>
                  <div className="text-amber-300/80 pt-1">
                    {isAr ? 'تأكد من رفع ملف الرخصة الصحيح قبل الاستمرار.' : 'Please verify you are uploading the correct license file before continuing.'}
                  </div>
                </div>
              )}
              {renewNameCheck && renewNameCheck.matched && (
                <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1" data-testid="agent-renew-name-match">
                  ✓ {isAr ? 'اسم الشركة يطابق الملف' : 'Company name matches the file'}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-[#2a2a4e] rounded-xl text-white" onClick={() => setRenewOpen(false)} disabled={renewing}>
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={async () => {
                  if (!renewForm.file) {
                    toast.error(isAr ? 'ارفع ملف الرخصة الجديدة' : 'Upload the new license file');
                    return;
                  }
                  if (!renewForm.expiry_date) {
                    toast.error(isAr ? 'تاريخ الانتهاء إلزامي' : 'Expiry date is required');
                    return;
                  }
                  setRenewing(true);
                  try {
                    await axios.post(`${API}/agent/companies/${company.id}/renew-license`, {
                      license_number:  renewForm.license_number,
                      unified_license_number: renewForm.unified_license_number,
                      issue_date:      renewForm.issue_date,
                      expiry_date:     renewForm.expiry_date,
                      license_file:    renewForm.file.base64,
                      license_file_mime: renewForm.file.mime,
                      license_file_name: renewForm.file.name,
                    }, { headers: getAuthHeaders() });
                    toast.success(isAr ? 'تم التجديد وأرشفة القديمة' : 'Renewed and archived the previous one');
                    setRenewOpen(false);
                    setRenewForm({ license_number: '', unified_license_number: '', issue_date: '', expiry_date: '', file: null });
                    setRenewNameCheck(null);
                    await load();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || err.message);
                  } finally {
                    setRenewing(false);
                  }
                }}
                disabled={renewing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                data-testid="agent-renew-save-btn"
              >
                {renewing ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <History className="h-4 w-4 me-2" />}
                {isAr ? 'تجديد' : 'Renew'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <PreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        base64={preview?.base64}
        mime={preview?.mime}
        name={preview?.name}
        language={language}
      />

      {/* iter-213 — Agent inline add/edit modal */}
      {agentModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !savingAgent && setAgentModal(false)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="agent-modal">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-bold flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-amber-400" />
                {isAr ? 'الوكيل المرتبط' : 'Linked Agent'}
              </h3>
              <button onClick={() => !savingAgent && setAgentModal(false)} className="p-1.5 rounded hover:bg-white/5 text-white/60"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-[#606060] mb-1 block">{isAr ? 'اسم الوكيل *' : 'Agent name *'}</label>
                <input
                  value={agentForm.agent_name}
                  onChange={(e) => setAgentForm((f) => ({ ...f, agent_name: e.target.value }))}
                  placeholder={isAr ? 'الاسم الكامل' : 'Full name'}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  data-testid="agent-modal-name"
                />
              </div>
              <div>
                <label className="text-xs text-[#606060] mb-1 block">{isAr ? 'البريد الإلكتروني' : 'Email'}</label>
                <input
                  type="email"
                  value={agentForm.agent_email}
                  onChange={(e) => setAgentForm((f) => ({ ...f, agent_email: e.target.value }))}
                  placeholder="email@example.com"
                  dir="ltr"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  data-testid="agent-modal-email"
                />
              </div>
              <div>
                <label className="text-xs text-[#606060] mb-1 block">{isAr ? 'رقم الهاتف' : 'Phone'}</label>
                <input
                  value={agentForm.agent_phone}
                  onChange={(e) => setAgentForm((f) => ({ ...f, agent_phone: e.target.value }))}
                  placeholder="+971 50 000 0000"
                  dir="ltr"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  data-testid="agent-modal-phone"
                />
              </div>
              <p className="text-[11px] text-[#606060] leading-relaxed">
                {isAr
                  ? 'يمكن تعديل هذه البيانات لاحقاً. عند إنشاء طلب وكيل وربطه، ستُعكس بيانات الوكيل تلقائياً هنا.'
                  : 'You can edit these later. When an agent task is created and linked, the agent info will be reflected here automatically.'}
              </p>
            </div>
            <div className="flex gap-2 p-4 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => setAgentModal(false)}
                disabled={savingAgent}
                className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-lg"
              >
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleSaveAgent}
                disabled={savingAgent}
                className="flex-1 bg-amber-600 hover:bg-amber-700 rounded-lg text-white"
                data-testid="agent-modal-save"
              >
                {savingAgent ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                {isAr ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* iter-213 — Contact person add/edit modal */}
      {contactForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !savingContact && setContactForm(null)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-white font-bold flex items-center gap-2">
                <Phone className="h-5 w-5 text-cyan-400" />
                {contactForm.id
                  ? (isAr ? 'تعديل جهة اتصال' : 'Edit contact')
                  : (isAr ? 'إضافة جهة اتصال' : 'Add contact')}
              </h3>
              <button onClick={() => !savingContact && setContactForm(null)} className="p-1.5 rounded hover:bg-white/5 text-white/60"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-[#606060] mb-1 block">{isAr ? 'الاسم' : 'Name'}</label>
                <input
                  value={contactForm.name}
                  onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={isAr ? 'اسم الشخص' : 'Person name'}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid="contact-modal-name"
                />
              </div>
              <div>
                <label className="text-xs text-[#606060] mb-1 block">{isAr ? 'رقم الهاتف' : 'Phone'}</label>
                <input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+971 50 000 0000"
                  dir="ltr"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid="contact-modal-phone"
                />
              </div>
              <div>
                <label className="text-xs text-[#606060] mb-1 block">{isAr ? 'البريد الإلكتروني' : 'Email'}</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                  dir="ltr"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid="contact-modal-email"
                />
              </div>
              <p className="text-[11px] text-[#606060] leading-relaxed">
                {isAr
                  ? 'يكفي إدخال حقل واحد على الأقل (اسم أو هاتف أو بريد).'
                  : 'At least one field is required (name, phone or email).'}
              </p>
            </div>
            <div className="flex gap-2 p-4 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => setContactForm(null)}
                disabled={savingContact}
                className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-lg"
              >
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleSaveContact}
                disabled={savingContact}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-white"
                data-testid="contact-modal-save"
              >
                {savingContact ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
                {isAr ? 'حفظ' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AgentPageShell>
  );
};

const InfoRow = ({ label, value, icon: Icon, full = false }) => {
  if (!value) return null;
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <p className="text-xs text-[#606060] mb-0.5 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <p className="text-sm text-white break-words">{value}</p>
    </div>
  );
};

// iter-276 — Dropdown menu row for the File Actions button.
const ActionMenuItem = ({ icon: Icon, label, onClick, color = 'text-white', testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${color} hover:bg-white/10 transition-colors text-start`}
  >
    {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
    <span className="flex-1">{label}</span>
  </button>
);

// iter-249 (Feb 2026) — Service Requests History tab on the company profile.
// Each row shows the CSR request no, service label, current stage, linked
// invoice (if past FEE_COLLECTION), and a deep-link to the tracking pipeline.
const STAGE_META = {
  REQUEST_RECEIVED:         { ar: 'تم استلام الطلب',       en: 'Received',         color: 'blue'    },
  REQUEST_ACCEPTED:         { ar: 'تم قبول الطلب',         en: 'Accepted',         color: 'sky'     },
  IN_PROGRESS:              { ar: 'جاري تنفيذ الطلب',     en: 'In Progress',      color: 'amber'   },
  FEE_COLLECTION:           { ar: 'تحصيل الرسوم',           en: 'Fee Collection',   color: 'orange'  },
  COMPLETED_AND_DISPATCHED: { ar: 'تم الانتهاء والإرسال', en: 'Completed',        color: 'emerald' },
};
const STAGE_PILL = {
  blue:    'bg-blue-500/20 text-blue-300 border-blue-500/30',
  sky:     'bg-sky-500/20 text-sky-300 border-sky-500/30',
  amber:   'bg-amber-500/20 text-amber-300 border-amber-500/30',
  orange:  'bg-orange-500/20 text-orange-300 border-orange-500/30',
  emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
};

function CompanyServiceRequestsSection({ requests, isAr, onAdd, onOpen }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.02 }}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6"
      data-testid="company-service-requests-section"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5 text-emerald-400" />
          <h2 className="font-semibold text-white">
            {isAr ? 'سجل الطلبات والخدمات' : 'Service Requests History'}
          </h2>
          <span className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/60">
            {requests.length}
          </span>
        </div>
        {/* iter-279c — "Insert Request" button removed from services tab
             (already exposed via the actions dropdown at the top of the page). */}
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-white/10 rounded-xl bg-black/30 text-[#808080] text-sm"
             data-testid="csr-empty">
          {isAr
            ? 'لم يُنشأ أي طلب خدمة لهذا الملف بعد. استخدم زر "الإجراءات" في الأعلى → "إدراج طلب خدمة".'
            : 'No service requests for this file yet. Use the "Actions" button at the top → "Insert Service Request".'}
        </div>
      ) : (
        <ul className="space-y-2">
          {requests.map((r) => {
            const meta = STAGE_META[r.status] || { ar: r.status, en: r.status, color: 'blue' };
            return (
              <li key={r.id}
                  onClick={() => onOpen(r)}
                  className="flex items-start justify-between gap-3 bg-black/30 border border-white/5 rounded-xl px-4 py-3 hover:bg-white/[0.04] cursor-pointer transition-all"
                  data-testid={`csr-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">
                      {isAr ? (r.main_service_name_ar || r.main_service_name_en) : (r.main_service_name_en || r.main_service_name_ar)}
                    </span>
                    {r.sub_service_name_ar && (
                      <span className="text-[12px] text-white/60">
                        · {isAr ? (r.sub_service_name_ar || r.sub_service_name_en) : (r.sub_service_name_en || r.sub_service_name_ar)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-white/50">
                    <span className="font-mono">{r.request_no}</span>
                    {r.invoice_no_display && (
                      <>
                        <span>·</span>
                        <span className="font-mono text-emerald-300">{r.invoice_no_display}</span>
                      </>
                    )}
                    <span>·</span>
                    <span className={`px-1.5 py-0.5 rounded border ${STAGE_PILL[meta.color]} font-medium`}>
                      {isAr ? meta.ar : meta.en}
                    </span>
                    {r.created_at && (
                      <>
                        <span>·</span>
                        <span>{new Date(r.created_at).toLocaleDateString(isAr ? 'ar-EG' : 'en-GB')}</span>
                      </>
                    )}
                  </div>
                  {r.notes && (
                    <div className="text-[12px] text-white/60 mt-1 line-clamp-2">{r.notes}</div>
                  )}
                </div>
                <div className="text-sm font-bold text-emerald-300 whitespace-nowrap">
                  {Number(r.price || 0).toLocaleString(isAr ? 'ar-EG' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-white/50 text-[10px]">AED</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.section>
  );
}

/* ── iter-276 (Feb 2026) — Multi-account Platform Linking Card ───────
 * The company can be linked to N merchant-platform accounts. Each
 * account has: email + optional phone + a status of either 'registered'
 * (live account we found on the merchant platform) or 'pending' (email
 * saved so the file auto-adopts when they later sign up).
 *
 * Backward compat: on first render we hydrate the list from the legacy
 * single fields `merchant_platform_email`/`merchant_platform_phone` if
 * `linked_merchant_accounts` is empty on the server payload.
 * ------------------------------------------------------------------ */
const _accId = () => `mp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
const _emailRe = /^\S+@\S+\.\S+$/;

function PlatformLinkingCard({ company, onSaved, isAr }) {
  const t = (ar, en) => (isAr ? ar : en);

  // Hydrate with backward-compat from legacy single field.
  const seedAccounts = React.useMemo(() => {
    if (Array.isArray(company?.linked_merchant_accounts) && company.linked_merchant_accounts.length) {
      return company.linked_merchant_accounts.map((a) => ({ id: a.id || _accId(), ...a }));
    }
    if (company?.merchant_platform_email) {
      return [{
        id: _accId(),
        email: company.merchant_platform_email,
        phone: company.merchant_platform_phone || '',
        status: 'registered',
      }];
    }
    return [];
  }, [company?.linked_merchant_accounts, company?.merchant_platform_email, company?.merchant_platform_phone]);

  const [accounts, setAccounts] = React.useState(seedAccounts);
  React.useEffect(() => { setAccounts(seedAccounts); }, [seedAccounts]);

  // iter-284 (Feb 2026) — Redesigned UI: ONE persistent search box at
  // the top. Typing debounces a call to /admin/merchant-users/search;
  // results render as a dropdown below the box. Clicking a result adds
  // it as a "registered" account to the list below with an "Active"
  // badge and a delete button. Typing again allows adding another.
  const [query, setQuery] = React.useState('');
  const [searchState, setSearchState] = React.useState({
    status: 'idle', // idle | loading | found | multi | not_found | error
    results: [],
    single: null,
    error: null,
  });
  // Track UI-scoped focus so the results dropdown only opens while
  // the field is active — dismissing it lets the accounts list breathe.
  const [showResults, setShowResults] = React.useState(false);
  const [pendingOpen, setPendingOpen] = React.useState(false);
  const [pendingEmail, setPendingEmail] = React.useState('');
  const [pendingPhone, setPendingPhone] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const dirty = JSON.stringify(accounts) !== JSON.stringify(seedAccounts);
  const allValid = accounts.every((a) => !a.email || _emailRe.test((a.email || '').trim()));

  // Debounced search
  React.useEffect(() => {
    const q = (query || '').trim();
    if (!q) {
      setSearchState({ status: 'idle', results: [], single: null, error: null });
      return;
    }
    setSearchState((s) => ({ ...s, status: 'loading' }));
    const timer = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/merchant-users/search`,
          { headers: getAuthHeaders(), params: { q, limit: 8 } },
        );
        if (Array.isArray(r.data?.results)) {
          setSearchState({
            status: r.data.results.length ? 'multi' : 'not_found',
            results: r.data.results, single: null, error: null,
          });
        } else if (r.data?.email || r.data?.phone) {
          // Single-hit legacy shape.
          setSearchState({ status: 'found', results: [r.data], single: r.data, error: null });
        } else {
          setSearchState({ status: 'not_found', results: [], single: null, error: null });
        }
      } catch (err) {
        const notFound = err?.response?.status === 404;
        setSearchState({
          status: notFound ? 'not_found' : 'error',
          results: [], single: null,
          error: notFound ? null : (err?.response?.data?.detail || err.message),
        });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const isAlreadyLinked = (user) => accounts.some((a) => {
    const ue = (user?.email || '').toLowerCase();
    const up = (user?.phone || '').trim();
    const ae = (a.email || '').toLowerCase();
    const ap = (a.phone || '').trim();
    return (ue && ae === ue) || (up && ap && ap === up);
  });

  const addFromSearch = (user) => {
    if (!user || isAlreadyLinked(user)) {
      toast.info(t('هذا الحساب مربوط بالفعل', 'This account is already linked'));
      return;
    }
    const newAcc = {
      id: _accId(),
      email: (user.email || '').trim().toLowerCase(),
      phone: (user.phone || '').trim(),
      remote_name: user.name || '',
      remote_id: user.id || '',
      remote_role: user.role || 'agent',
      status: 'registered',
    };
    setAccounts((prev) => [...prev, newAcc]);
    setQuery('');
    setSearchState({ status: 'idle', results: [], single: null, error: null });
    setShowResults(false);
    toast.success(t(`تم ربط ${user.name || user.email}`, `Linked ${user.name || user.email}`));
  };

  const addPending = () => {
    const email = (pendingEmail || '').trim().toLowerCase();
    if (!_emailRe.test(email)) {
      toast.error(t('صيغة البريد غير صحيحة', 'Invalid email'));
      return;
    }
    if (accounts.some((a) => (a.email || '').toLowerCase() === email)) {
      toast.info(t('هذا البريد مربوط بالفعل', 'This email is already linked'));
      return;
    }
    setAccounts((prev) => [...prev, {
      id: _accId(),
      email,
      phone: (pendingPhone || '').trim(),
      status: 'pending',
    }]);
    setPendingEmail('');
    setPendingPhone('');
    setPendingOpen(false);
    toast.success(t('تم إضافة الحساب — سيُربط عند تسجيله', 'Added — will link on sign-up'));
  };

  const removeAccount = (id) => {
    setAccounts((prev) => prev.filter((a) => a.id !== id));
  };

  const save = async () => {
    if (!allValid) {
      toast.error(t('صيغة أحد البريدات الإلكترونية غير صحيحة', 'One or more emails are invalid'));
      return;
    }
    const clean = accounts
      .map((a) => ({
        id: a.id,
        email: (a.email || '').trim(),
        phone: (a.phone || '').trim(),
        status: a.status === 'pending' ? 'pending' : 'registered',
      }))
      .filter((a) => a.email);
    setSaving(true);
    try {
      const payload = {
        linked_merchant_accounts: clean,
        merchant_platform_email: clean[0]?.email || null,
        merchant_platform_phone: clean[0]?.phone || null,
      };
      const r = await axios.patch(`${API}/agent/companies/${company.id}`, payload, {
        headers: getAuthHeaders(),
      });
      onSaved?.(r.data?.data || payload);
      toast.success(t('تم حفظ الربط', 'Link saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.04] to-white/[0.02] p-6 space-y-5 mb-6"
      data-testid="platform-linking-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
          <Link2 className="h-5 w-5 text-cyan-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-white">
            {t('الربط بمنصة التجار', 'Link to Merchant Platform')}
          </h2>
          <p className="text-[11px] text-white/50 mt-0.5 leading-relaxed">
            {t(
              'ابحث بالبريد أو الهاتف واضغط على النتيجة لربطها بملف الشركة. يمكنك ربط أكثر من حساب.',
              'Search by email or phone and click a result to link it. Multiple accounts supported.',
            )}
          </p>
        </div>
        <span
          className="text-[11px] px-2 py-1 rounded-md bg-black/30 border border-white/10 text-white/70"
          data-testid="platform-linking-count-badge"
        >
          {accounts.length} {t('حساب', accounts.length === 1 ? 'account' : 'accounts')}
        </span>
      </div>

      {/* iter-284 — Persistent search box */}
      <div className="relative">
        <label className="flex items-center gap-2 text-[11px] text-cyan-300/90 mb-1.5 font-semibold">
          <Search className="h-3 w-3" />
          {t('ابحث في قاعدة حسابات منصة التجار', 'Search the merchant platform database')}
        </label>
        <div className="relative">
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            placeholder={t('البريد الإلكتروني أو رقم الهاتف...', 'Email or phone number...')}
            dir="ltr"
            className="w-full px-3 py-2.5 pe-9 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 focus:border-cyan-500/60"
            data-testid="platform-linking-search-input"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); setShowResults(false); }}
              className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-white/40 hover:text-white hover:bg-white/10"
              data-testid="platform-linking-search-clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Results dropdown */}
        {showResults && query.trim() && (
          <div
            className="absolute z-30 top-full mt-1.5 w-full rounded-xl bg-[#0a0a0a] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden"
            data-testid="platform-linking-search-results"
          >
            {searchState.status === 'loading' && (
              <div className="px-4 py-3 text-[12px] text-white/60 inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('جاري البحث...', 'Searching...')}
              </div>
            )}
            {searchState.status === 'not_found' && (
              <div className="px-4 py-3 text-[12px] text-amber-300/90">
                {t('لا يوجد حساب مطابق. جرّب "غير مسجل" لحفظ البريد للربط لاحقاً.',
                   'No matching account. Try "Unregistered" to save the email for future linking.')}
              </div>
            )}
            {searchState.status === 'error' && (
              <div className="px-4 py-3 text-[12px] text-red-300">
                {t('خطأ في البحث: ', 'Search error: ')}{searchState.error}
              </div>
            )}
            {(searchState.status === 'multi' || searchState.status === 'found') && (
              <ul className="max-h-72 overflow-y-auto">
                {searchState.results.map((u, i) => {
                  const linked = isAlreadyLinked(u);
                  return (
                    <li key={u.id || `${u.email}-${i}`}>
                      <button
                        type="button"
                        onClick={() => !linked && addFromSearch(u)}
                        disabled={linked}
                        className={`w-full text-start px-4 py-2.5 flex items-center gap-3 transition ${
                          linked
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:bg-emerald-500/10'
                        }`}
                        data-testid={`platform-linking-search-result-${i}`}
                      >
                        <div className="h-8 w-8 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-[11px] font-bold text-cyan-200">
                          {(u.name || u.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-semibold truncate">
                            {u.name || u.email || '—'}
                          </div>
                          <div className="text-white/50 text-[11px] truncate" dir="ltr">
                            {[u.email, u.phone].filter(Boolean).join(' · ')}
                            {u.role ? ` · ${u.role}` : ''}
                          </div>
                        </div>
                        {linked ? (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/50 border border-white/10">
                            {t('مربوط', 'Linked')}
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 inline-flex items-center gap-1">
                            <Plus className="h-2.5 w-2.5" /> {t('ربط', 'Link')}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Linked accounts list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-white/70 inline-flex items-center gap-1.5">
            <Check className="h-3 w-3 text-emerald-400" />
            {t('الحسابات المربوطة بملف الشركة', 'Accounts linked to this company file')}
          </h3>
        </div>
        {accounts.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-white/10 rounded-xl">
            <Link2 className="h-6 w-6 text-white/25 mx-auto mb-2" />
            <p className="text-white/50 text-[12px]">
              {t('لا توجد حسابات مربوطة بعد — ابدأ بالبحث في الأعلى.',
                 'No linked accounts yet — start by searching above.')}
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="platform-linking-linked-list">
            {accounts.map((acc, idx) => (
              <li
                key={acc.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/10"
                data-testid={`platform-linking-linked-row-${idx}`}
              >
                <div
                  className={`h-9 w-9 rounded-full flex items-center justify-center text-[12px] font-bold ${
                    acc.status === 'registered'
                      ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-200'
                      : 'bg-amber-500/15 border border-amber-500/30 text-amber-200'
                  }`}
                >
                  {(acc.remote_name || acc.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold truncate">
                    {acc.remote_name || acc.email || '—'}
                  </div>
                  <div className="text-white/50 text-[11px] truncate" dir="ltr">
                    {[acc.email, acc.phone].filter(Boolean).join(' · ')}
                    {acc.remote_role ? ` · ${acc.remote_role}` : ''}
                  </div>
                </div>
                {acc.status === 'registered' ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    {t('فعّال', 'Active')}
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-200 border border-amber-500/30 inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    {t('غير مسجل', 'Unregistered')}
                  </span>
                )}
                <button
                  onClick={() => removeAccount(acc.id)}
                  className="p-1.5 rounded-md text-red-300/70 hover:text-red-300 hover:bg-red-500/10 transition"
                  title={t('حذف', 'Remove')}
                  data-testid={`platform-linking-linked-row-${idx}-remove`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add unregistered — collapsible inline form */}
      <div className="border-t border-white/5 pt-4">
        {!pendingOpen ? (
          <button
            onClick={() => setPendingOpen(true)}
            className="text-[12px] text-amber-300/90 hover:text-amber-200 inline-flex items-center gap-1.5"
            data-testid="platform-linking-add-pending-toggle"
          >
            <Plus className="h-3 w-3" />
            {t('إضافة بريد لعميل غير مسجل (يُربط تلقائياً عند التسجيل)',
               'Add unregistered email (auto-links on sign-up)')}
          </button>
        ) : (
          <div className="rounded-xl bg-amber-500/[0.05] border border-amber-500/25 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-[12px] text-amber-200 font-semibold">
                {t('حساب غير مسجل', 'Unregistered account')}
              </h4>
              <button
                onClick={() => { setPendingOpen(false); setPendingEmail(''); setPendingPhone(''); }}
                className="p-1 rounded text-white/40 hover:text-white/70"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <input
              value={pendingEmail}
              onChange={(e) => setPendingEmail(e.target.value)}
              placeholder="example@mail.com"
              dir="ltr"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-amber-500/60"
              data-testid="platform-linking-pending-email"
            />
            <input
              value={pendingPhone}
              onChange={(e) => setPendingPhone(e.target.value)}
              placeholder="+9715…"
              dir="ltr"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-amber-500/60"
              data-testid="platform-linking-pending-phone"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={addPending}
                disabled={!_emailRe.test((pendingEmail || '').trim())}
                className="bg-amber-500/80 hover:bg-amber-500 text-black text-xs font-semibold"
                data-testid="platform-linking-pending-add-btn"
              >
                <Plus className="h-3 w-3 me-1" /> {t('إضافة', 'Add')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Save all */}
      {accounts.length > 0 && (
        <div className="flex items-center justify-end gap-2 pt-1">
          {!dirty && (
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
              <Check className="h-2.5 w-2.5" /> {t('محفوظ', 'Saved')}
            </span>
          )}
          <Button
            onClick={save}
            disabled={saving || !dirty || !allValid}
            className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold disabled:opacity-40"
            size="sm"
            data-testid="platform-linking-save-btn"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin me-1" /> : <Save className="h-3 w-3 me-1" />}
            {t('حفظ الكل', 'Save All')}
          </Button>
        </div>
      )}
    </motion.section>
  );
}

/* One row in the multi-account linking card.
   iter-279c — When status='registered', shows ONE unified search field
   (email or phone) that hits /admin/merchant-users/search. On found, the
   row's email/phone/name are populated from the remote user. When
   status='pending' (unregistered), two separate email+phone fields render
   so the operator can save the intended contact info for auto-linking later. */
function LinkedAccountRow({ acc, idx, isAr, onChange, onRemove }) {
  const t = (ar, en) => (isAr ? ar : en);
  const [lookup, setLookup] = React.useState({ state: 'idle', data: null, error: null, results: [] });
  const [searchQ, setSearchQ] = React.useState(acc.email || acc.phone || '');
  const isRegistered = acc.status === 'registered';
  const emailValid = !acc.email || _emailRe.test((acc.email || '').trim());

  React.useEffect(() => {
    if (!isRegistered) { setLookup({ state: 'idle', data: null, error: null, results: [] }); return; }
    const q = (searchQ || '').trim();
    if (!q) { setLookup({ state: 'idle', data: null, error: null, results: [] }); return; }
    const timer = setTimeout(async () => {
      setLookup({ state: 'loading', data: null, error: null, results: [] });
      try {
        const r = await axios.get(
          `${API}/admin/merchant-users/search`,
          { headers: getAuthHeaders(), params: { q, limit: 8 } },
        );
        // iter-279d — Response is either a single user (backward-compat) OR
        // a multi-hit shape {results: [...], count: N}. When multi, we show
        // the list so the operator picks; when single, we auto-fill the row.
        if (Array.isArray(r.data?.results)) {
          setLookup({ state: 'multi', data: null, error: null, results: r.data.results });
        } else {
          setLookup({ state: 'found', data: r.data, error: null, results: [] });
          onChange({
            email: (r.data?.email || '').trim().toLowerCase(),
            phone: (r.data?.phone || '').trim(),
            remote_name: r.data?.name || '',
            remote_id: r.data?.id || '',
          });
        }
      } catch (err) {
        const notFound = err?.response?.status === 404;
        setLookup({
          state: notFound ? 'not_found' : 'error',
          data: null,
          results: [],
          error: notFound ? null : (err?.response?.data?.detail || err.message),
        });
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, isRegistered]);

  const pickResult = (u) => {
    onChange({
      email: (u?.email || '').trim().toLowerCase(),
      phone: (u?.phone || '').trim(),
      remote_name: u?.name || '',
      remote_id: u?.id || '',
    });
    setLookup({ state: 'found', data: u, error: null, results: [] });
    setSearchQ(u?.email || u?.phone || '');
  };

  return (
    <div
      className={`p-4 rounded-xl bg-black/30 border ${
        isRegistered ? 'border-emerald-500/20' : 'border-amber-500/20'
      }`}
      data-testid={`platform-linking-row-${idx}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] text-white/40 font-mono">#{idx + 1}</span>
          {/* iter-279c — Registration status toggle FIRST, before any fields. */}
          <div className="inline-flex gap-1 p-0.5 rounded-md border border-white/10 bg-black/40">
            <button
              onClick={() => onChange({ status: 'registered' })}
              className={`text-[11px] px-2.5 py-1 rounded transition ${isRegistered
                ? 'bg-emerald-500/25 text-emerald-100' : 'text-white/50 hover:text-white'}`}
              data-testid={`platform-linking-row-${idx}-set-registered`}
            >
              {t('عميل مسجل', 'Registered')}
            </button>
            <button
              onClick={() => onChange({ status: 'pending' })}
              className={`text-[11px] px-2.5 py-1 rounded transition ${!isRegistered
                ? 'bg-amber-500/25 text-amber-100' : 'text-white/50 hover:text-white'}`}
              data-testid={`platform-linking-row-${idx}-set-pending`}
            >
              {t('غير مسجل', 'Unregistered')}
            </button>
          </div>
        </div>
        <button
          onClick={onRemove}
          className="text-red-300/80 hover:text-red-300 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-red-500/10"
          data-testid={`platform-linking-row-${idx}-remove`}
        >
          <X className="h-3 w-3" /> {t('حذف', 'Remove')}
        </button>
      </div>

      {isRegistered ? (
        <>
          {/* Single unified search: email OR phone. */}
          <label className="flex items-center gap-2 text-[11px] text-emerald-300/90 mb-1.5 font-semibold">
            <Search className="h-3 w-3" />
            {t('ابحث بالبريد أو رقم الهاتف', 'Search by email or phone')}
          </label>
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={t('example@mail.com  أو  +9715...', 'example@mail.com  or  +9715...')}
            dir="ltr"
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 focus:border-emerald-500/60"
            data-testid={`platform-linking-row-${idx}-search`}
          />

          <div className="mt-3">
            {lookup.state === 'loading' && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-white/60">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('جاري البحث في قاعدة بيانات التجار...', 'Searching merchant database...')}
              </span>
            )}
            {lookup.state === 'multi' && (
              <div className="p-2 rounded-md bg-black/40 border border-white/10 space-y-1"
                   data-testid={`platform-linking-row-${idx}-multi`}>
                <div className="text-[11px] text-white/60 mb-1 flex items-center gap-1.5">
                  <Search className="h-3 w-3" />
                  {t(`عُثر على ${lookup.results.length} نتيجة — اختر تاجراً:`,
                     `Found ${lookup.results.length} results — pick one:`)}
                </div>
                <ul className="space-y-1 max-h-56 overflow-y-auto">
                  {lookup.results.map((u) => (
                    <li key={u.id || u.email}>
                      <button
                        type="button"
                        onClick={() => pickResult(u)}
                        className="w-full text-start p-2 rounded-md bg-white/[0.02] border border-white/10 hover:bg-emerald-500/[0.06] hover:border-emerald-500/30 transition"
                        data-testid={`platform-linking-row-${idx}-pick`}
                      >
                        <div className="text-sm text-white font-medium truncate">{u.name || u.email || '—'}</div>
                        <div className="text-[10px] text-white/60 flex flex-wrap gap-x-3" dir="ltr">
                          {u.email && <span className="font-mono">{u.email}</span>}
                          {u.phone && <span className="font-mono">{u.phone}</span>}
                          {u.membership_number && <span className="font-mono">#{u.membership_number}</span>}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lookup.state === 'found' && (
              <div className="p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-200">
                <div className="flex items-center gap-1.5 font-semibold mb-1">
                  <Check className="h-3 w-3" />
                  {t('حساب موجود على منصة التجار', 'Account exists on merchant platform')}
                </div>
                <div className="text-white/80 text-[11px] grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
                  {lookup.data?.name  && <div>{t('الاسم:', 'Name:')} <span className="text-white font-medium">{lookup.data.name}</span></div>}
                  {lookup.data?.email && <div>{t('البريد:', 'Email:')} <span className="text-white font-mono" dir="ltr">{lookup.data.email}</span></div>}
                  {lookup.data?.phone && <div>{t('الهاتف:', 'Phone:')} <span className="text-white font-mono" dir="ltr">{lookup.data.phone}</span></div>}
                  {lookup.data?.role  && <div>{t('الدور:', 'Role:')} <span className="text-white">{lookup.data.role}</span></div>}
                </div>
              </div>
            )}
            {lookup.state === 'not_found' && (
              <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/30">
                <XCircle className="h-3 w-3" />
                {t('لا يوجد تاجر بهذه البيانات — حوّل إلى "غير مسجل" لحفظه للربط التلقائي لاحقاً',
                   'No merchant with these details — switch to "Unregistered" to save for auto-linking later')}
              </span>
            )}
            {lookup.state === 'error' && (
              <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-red-500/10 text-red-300 border border-red-500/30">
                <XCircle className="h-3 w-3" />
                {t('فشل البحث:', 'Search failed:')} {String(lookup.error || '').slice(0, 80)}
              </span>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Unregistered — two separate fields for future auto-link. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-2 text-[11px] text-amber-300/90 mb-1.5 font-semibold">
                <Mail className="h-3 w-3" /> {t('البريد الإلكتروني', 'Email')}
              </label>
              <input
                type="email"
                value={acc.email || ''}
                onChange={(e) => onChange({ email: e.target.value })}
                placeholder="agent@example.com"
                dir="ltr"
                className={`w-full px-3 py-2 rounded-lg bg-black/40 border text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 ${
                  emailValid ? 'border-white/10 focus:border-amber-500/60'
                             : 'border-red-500/50 focus:border-red-500'
                }`}
                data-testid={`platform-linking-row-${idx}-email`}
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-[11px] text-amber-300/90 mb-1.5 font-semibold">
                <Phone className="h-3 w-3" /> {t('رقم الهاتف', 'Phone')}
              </label>
              <input
                type="tel"
                value={acc.phone || ''}
                onChange={(e) => onChange({ phone: e.target.value })}
                placeholder="+9715..."
                dir="ltr"
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 focus:border-amber-500/60"
                data-testid={`platform-linking-row-${idx}-phone`}
              />
            </div>
          </div>
          <p className="text-[10px] text-amber-300/70 mt-2 leading-relaxed">
            {t(
              'سنحفظ البريد والهاتف. عند تسجيل هذا الشخص بنفس البريد على منصة التجار سيُربط الملف تلقائياً.',
              'We save the email + phone. When they sign up on the merchant platform with this email, the company file will auto-link.',
            )}
          </p>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// iter-262 (Feb 2026) — Merged Residencies + Other Documents section.
// Each sub-section owns its own search box; the Other Documents sub-
// section takes over the display responsibility from AgentOtherDocsPage
// (which is now an insert-only surface).
// ────────────────────────────────────────────────────────────────────
function ResidencyAndOtherDocsSections({
  residencies, otherDocs, isAr, language,
  onAddResidency, onDeleteResidency, onDeleteOtherDoc, openPreview,
  hideResidencies = false, hideOtherDocs = false,
}) {
  const [resQ, setResQ] = React.useState('');
  const [otherQ, setOtherQ] = React.useState('');

  const resFiltered = React.useMemo(() => {
    if (!resQ.trim()) return residencies;
    const q = resQ.trim().toLowerCase();
    return residencies.filter((r) =>
      (r.full_name_ar || '').toLowerCase().includes(q)
      || (r.full_name_en || '').toLowerCase().includes(q)
      || (r.emirates_id_number || '').toLowerCase().includes(q)
      || (r.passport_number || '').toLowerCase().includes(q)
      || (r.visa_number || '').toLowerCase().includes(q)
      || (r.nationality || '').toLowerCase().includes(q)
    );
  }, [residencies, resQ]);

  const docsFiltered = React.useMemo(() => {
    if (!otherQ.trim()) return otherDocs;
    const q = otherQ.trim().toLowerCase();
    return otherDocs.filter((d) =>
      (d.title || '').toLowerCase().includes(q)
      || (d.title_ar || '').toLowerCase().includes(q)
      || (d.title_en || '').toLowerCase().includes(q)
      || (d.document_file_name || '').toLowerCase().includes(q)
    );
  }, [otherDocs, otherQ]);

  return (
    <>
      {!hideResidencies && (<>
      {/* Residencies / Visas / Emirates IDs */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-lg font-bold flex items-center gap-2 text-white">
          <IdCard className="h-5 w-5 text-cyan-400" />
          {isAr ? 'الإقامات والتأشيرات والهويات' : 'Residencies / Visas / Emirates IDs'}
          <span className="text-xs text-[#808080]">({residencies.length})</span>
        </h2>
        {residencies.length > 0 && (
          <div className="relative w-64 max-w-full">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
            <input
              value={resQ}
              onChange={(e) => setResQ(e.target.value)}
              placeholder={isAr ? 'ابحث بالاسم أو رقم الهوية…' : 'Search by name or ID…'}
              className="w-full h-9 px-3 pe-9 rounded-lg bg-black/40 border border-white/10 text-white text-[12px] focus:outline-none focus:border-cyan-500/60"
              data-testid="agent-residency-search"
            />
          </div>
        )}
      </div>

      {residencies.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-2xl">
          <IdCard className="h-8 w-8 text-[#606060] mx-auto mb-2" />
          <p className="text-[#808080] mb-3">
            {isAr ? 'لا توجد سجلات مضافة لهذه الشركة' : 'No records added for this company'}
          </p>
          <Button
            onClick={onAddResidency}
            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl"
          >
            <Plus className="h-4 w-4 me-2" />
            {isAr ? 'أضف أول سجل' : 'Add first record'}
          </Button>
        </div>
      ) : resFiltered.length === 0 ? (
        <div className="text-center py-6 text-[12px] text-white/50 border border-dashed border-white/10 rounded-2xl mb-6">
          {isAr ? 'لا توجد نتائج تطابق البحث' : 'No results match your search'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {resFiltered.map((r) => {
            const label = DOC_TYPE_LABELS[r.doc_type] || DOC_TYPE_LABELS.residency;
            return (
              <div
                key={r.id}
                className="bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 rounded-2xl p-4 transition-all"
                data-testid={`agent-residency-card-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <span
                      className="inline-block px-2 py-0.5 rounded-md text-xs mb-1"
                      style={{ backgroundColor: `${label.color}20`, color: label.color, border: `1px solid ${label.color}40` }}
                    >
                      {isAr ? label.ar : label.en}
                    </span>
                    <p className="font-semibold flex items-center gap-1 text-white">
                      <User className="h-3.5 w-3.5" /> {isAr ? (r.full_name_ar || r.full_name_en) : (r.full_name_en || r.full_name_ar)}
                    </p>
                    {(isAr ? r.full_name_en : r.full_name_ar) && (
                      <p className="text-xs text-[#808080] mt-0.5">{isAr ? r.full_name_en : r.full_name_ar}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {r.document_file && (
                      <>
                        <button
                          onClick={() => openPreview(r.document_file, r.document_file_mime, r.document_file_name || r.full_name_en || 'document')}
                          className="p-1.5 rounded-md text-cyan-400 hover:bg-cyan-500/10"
                          title={isAr ? 'معاينة' : 'Preview'}
                          data-testid={`agent-residency-preview-${r.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <a
                          href={`data:${r.document_file_mime || 'application/octet-stream'};base64,${r.document_file}`}
                          download={r.document_file_name || 'document'}
                          className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"
                          title={isAr ? 'تحميل' : 'Download'}
                          data-testid={`agent-residency-download-${r.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </>
                    )}
                    <button
                      onClick={() => onDeleteResidency(r.id)}
                      className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10"
                      data-testid={`agent-residency-delete-${r.id}`}
                      title={isAr ? 'حذف' : 'Delete'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-[#a0a0a0] space-y-0.5">
                  {r.emirates_id_number && <div>ID: {r.emirates_id_number}</div>}
                  {r.passport_number && <div>{isAr ? 'جواز:' : 'Passport:'} {r.passport_number}</div>}
                  {r.visa_number && <div>{isAr ? 'التأشيرة:' : 'Visa #:'} {r.visa_number}</div>}
                  {r.nationality && <div>{isAr ? 'الجنسية:' : 'Nationality:'} {r.nationality}</div>}
                  {r.expiry_date && (
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <Calendar className="h-3 w-3" />
                      <span>{isAr ? 'ينتهي:' : 'Expiry:'} {r.expiry_date}</span>
                      <ExpiryBadge dateStr={r.expiry_date} language={language} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}

      {!hideOtherDocs && (<>
      {/* Other Documents — always shown as a section (empty state kept
         friendly so the operator understands they need to add via the
         "إضافة وثائق أخرى" button at the top of the file). */}
      <div className="flex items-center justify-between mt-6 mb-3 gap-3 flex-wrap">
        <h2 className="text-lg font-bold flex items-center gap-2 text-white">
          <FileArchive className="h-5 w-5 text-yellow-400" />
          {isAr ? 'وثائق أخرى' : 'Other Documents'}
          <span className="text-xs text-[#808080]">({otherDocs.length})</span>
        </h2>
        {otherDocs.length > 0 && (
          <div className="relative w-64 max-w-full">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
            <input
              value={otherQ}
              onChange={(e) => setOtherQ(e.target.value)}
              placeholder={isAr ? 'ابحث بعنوان الوثيقة…' : 'Search by title…'}
              className="w-full h-9 px-3 pe-9 rounded-lg bg-black/40 border border-white/10 text-white text-[12px] focus:outline-none focus:border-yellow-500/60"
              data-testid="agent-other-docs-search"
            />
          </div>
        )}
      </div>

      {otherDocs.length === 0 ? (
        <div className="text-center py-6 text-[12px] text-white/50 border border-dashed border-white/10 rounded-2xl mb-6">
          <FileArchive className="h-6 w-6 text-white/30 mx-auto mb-1.5" />
          {isAr
            ? 'لا توجد وثائق أخرى — استخدم زر "إضافة وثائق أخرى" أعلى الصفحة.'
            : 'No other documents yet — use "Add other documents" at the top of the page.'}
        </div>
      ) : docsFiltered.length === 0 ? (
        <div className="text-center py-6 text-[12px] text-white/50 border border-dashed border-white/10 rounded-2xl mb-6">
          {isAr ? 'لا توجد نتائج تطابق البحث' : 'No results match your search'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {docsFiltered.map((d) => (
            <div key={d.id} className="bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 rounded-2xl p-4"
                 data-testid={`agent-other-doc-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-white truncate">
                    {isAr ? (d.title_ar || d.title || d.title_en) : (d.title_en || d.title || d.title_ar)}
                  </p>
                  {d.title_ar && d.title_en && (d.title_ar !== d.title_en) && (
                    <p className="text-[10px] text-white/40 truncate mt-0.5" dir={isAr ? 'ltr' : 'rtl'}>
                      {isAr ? d.title_en : d.title_ar}
                    </p>
                  )}
                  <p className="text-xs text-[#808080] truncate mt-0.5">
                    {d.document_file_name || d.document_file_mime}
                  </p>
                </div>
                <div className="flex gap-1">
                  {d.document_file && (
                    <>
                      <button
                        onClick={() => openPreview(d.document_file, d.document_file_mime, d.document_file_name || d.title)}
                        className="p-1.5 rounded-md text-cyan-400 hover:bg-cyan-500/10"
                        title={isAr ? 'معاينة' : 'Preview'}
                        data-testid={`agent-other-doc-preview-${d.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <a
                        href={`data:${d.document_file_mime || 'application/octet-stream'};base64,${d.document_file}`}
                        download={d.document_file_name || d.title}
                        className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10"
                        title={isAr ? 'تحميل' : 'Download'}
                        data-testid={`agent-other-doc-download-${d.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    </>
                  )}
                  <button
                    onClick={() => onDeleteOtherDoc(d.id)}
                    className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10"
                    title={isAr ? 'حذف' : 'Delete'}
                    data-testid={`agent-other-doc-delete-${d.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </>
  );
}


export default AgentCompanyDetailPage;

// ─────────────────────────────────────────────────────────────────
// iter-278 (Feb 2026) — Renewals list section (License Renewals tab)
// ─────────────────────────────────────────────────────────────────
function RenewalsListSection({ companyId, onNew, onOpen, isAr }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [list, m] = await Promise.all([
          axios.get(`${API}/admin/agent-companies/${companyId}/renewals`,
                    { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/agent-companies/${companyId}/renewals-meta`,
                    { headers: getAuthHeaders() }),
        ]);
        setItems(list.data?.data || []);
        setMeta(m.data || {});
      } catch (e) {
        toast.error(e.response?.data?.detail || (isAr ? 'فشل تحميل التجديدات' : 'Failed to load renewals'));
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, isAr]);

  const badgeFor = (r) => r.audited
    ? { ar: 'مُدقَّق', en: 'Audited', cls: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' }
    : { ar: 'قيد التدقيق', en: 'Pending audit', cls: 'bg-amber-500/15 border-amber-500/40 text-amber-200' };

  return (
    <section className="space-y-4" data-testid="agent-renewals-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <History className="h-5 w-5 text-emerald-400" />
            {isAr ? 'طلبات تجديد الرخصة' : 'License Renewal Requests'}
          </h2>
          {meta && (
            <p className="text-xs text-[#808080] mt-1">
              {isAr
                ? `التجديد القادم سيكون التجديد رقم ${meta.next_renewal_index} · عدد التجديدات المسجّلة: ${meta.existing_renewals_count}`
                : `Next renewal will be #${meta.next_renewal_index} · Recorded renewals: ${meta.existing_renewals_count}`}
            </p>
          )}
        </div>
        {/* iter-280 — "New renewal request" button removed here
             (already exposed via the Actions dropdown → "تجديد الرخصة التجارية"). */}
      </div>

      {loading ? (
        <div className="text-white/50 flex items-center gap-2 py-8">
          <Loader2 className="h-4 w-4 animate-spin" />
          {isAr ? 'جارٍ التحميل…' : 'Loading…'}
        </div>
      ) : items.length === 0 ? (
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-dashed border-white/10 text-center">
          <p className="text-white/60 text-sm">
            {isAr
              ? 'لا توجد طلبات تجديد مسجّلة بعد.'
              : 'No renewal requests recorded yet.'}
          </p>
          <p className="text-white/40 text-xs mt-1">
            {isAr
              ? 'استخدم زر "الإجراءات" في أعلى الصفحة → "تجديد الرخصة التجارية" لبدء تسجيل تجديد الرخصة.'
              : 'Use the "Actions" button at the top → "Renew Commercial License" to start recording a renewal.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((r) => {
            const b = badgeFor(r);
            return (
              <button
                key={r.id}
                onClick={() => onOpen(r.id)}
                className="text-start p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-emerald-500/40 transition-colors"
                data-testid={`agent-renewal-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="text-emerald-300 text-xs font-medium">
                      {isAr ? `التجديد رقم ${r.renewal_index}` : `Renewal #${r.renewal_index}`}
                    </div>
                    <div className="text-white text-sm mt-1">
                      {r.license_number || (isAr ? '(بدون رقم رخصة)' : '(no license #)')}
                    </div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] border ${b.cls}`}>
                    {isAr ? b.ar : b.en}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-white/60">
                  {r.expiry_date && (
                    <div>{isAr ? 'انتهاء:' : 'Expiry:'} {r.expiry_date}</div>
                  )}
                  <div>{isAr ? 'المبلغ:' : 'Amount:'} {(r.renewal_amount || 0).toFixed(2)} AED</div>
                  {r.has_renewal_commission && (
                    <div className="text-purple-300">
                      {isAr ? `عمولة: ${(r.commission_amount || 0).toFixed(2)}` : `Commission: ${(r.commission_amount || 0).toFixed(2)}`}
                    </div>
                  )}
                  {r.has_agent_commission && (
                    <div className="text-cyan-300">
                      {isAr ? `عمولة الوكيل: ${(r.agent_commission_amount || 0).toFixed(2)}` : `Agent: ${(r.agent_commission_amount || 0).toFixed(2)}`}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

